// Rows for items removed through an exception resolution, keyed per source order so ids stay unique
// across a split parent and its children.
const toRemovedRows = (mappings, order) =>
    (mappings || []).map((mapping, index) => ({
        ...mapping.item,
        order,
        isResolutionRemoved: true,
        rowId: `removed-${order?.order_id}-${index}`,
    }));

/**
 * Build the Items tab rows for an order. Each row is an item with its `order` attached.
 * On a split parent, the parent's own items come first, then each child's items in the order given,
 * each carrying its child order so order-based columns show the child's state.
 * Resolution-removed items come last, flagged `isResolutionRemoved`, the parent's first and then each
 * child's (exception mappings on the child whose item no longer has an order).
 * Removed rows carry a `rowId` that is unique across the parent and its children.
 *
 * @param {object} params
 * @param {object} params.order The order shown on the page.
 * @param {object[]} [params.childOrders] Split children (from `useSplitChildren`). Read only when `order` is a split parent.
 * @param {object[]} [params.resolutionRemovedItems] Exception mappings whose item was removed from `order`.
 * @returns {object[]} Table rows.
 */
export const buildItemRows = ({ order, childOrders, resolutionRemovedItems }) => {
    const children = order?.order_status === 'split' ? childOrders || [] : [];
    const regularItems = (order?.itemsByOrderId || []).map((item) => ({ ...item, order }));
    const childItems = children.flatMap((child) =>
        (child?.itemsByOrderId || []).map((item) => ({ ...item, order: child }))
    );
    const removedItems = toRemovedRows(resolutionRemovedItems, order);
    const childRemovedItems = children.flatMap((child) =>
        toRemovedRows(
            (child?.exceptions || []).filter((mapping) => mapping?.item?.order_id === null),
            child
        )
    );
    return [...regularItems, ...childItems, ...removedItems, ...childRemovedItems];
};
